import { pathOr } from "ramda";
import * as Yup from "yup";

Yup.string().nullable;

type YupFieldValidation = {
  method: string;
  args: any[];
};

type YupFieldSchema = {
  type: string;
  required?: boolean;
  validations?: YupFieldValidation[];
};

const DECIMAL_PATTERN =
  /^[\u06F0-\u06F9\u0030-\u0039]+(\.[\u06F0-\u06F9\u0030-\u0039]+)?$/;

const persianToEnglishDigits = (value: string) =>
  value.replace(/[۰-۹]/g, (digit) => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit).toString());

const parseDecimalValue = (value: string) =>
  parseFloat(persianToEnglishDigits(value));

const isNumericType = (type: string) =>
  type === "int" || type === "float" || type === "decimal";

const isDecimalType = (type: string) => type === "float" || type === "decimal";

const schemaGenerator = {
  install(app: any) {
    const t = app.config.globalProperties.$lang;
    const globalProperties = app.config.globalProperties;

    // getLang("errors.minLength", [props.label, limit])

    const getLang = (key: string, params: any[]) => {
      return t(key, params);
    };

    // the object schema creator function

    function createYupSchema(field: {
      label: string;
      validation: string;
    }): any {
      let schemaParams: YupFieldSchema | any = {};

      const convertToSchema = () => {
        const obj: any = {};
        const valArr = field.validation.split("|");
        obj.label = field.label;

        const isRequired = valArr.find((item) => item === "required");
        isRequired ? (obj.required = true) : (obj.required = false);

        const isNullable = valArr.find((item) => item === "nullable");
        isNullable ? (obj.nullable = true) : (obj.nullable = false);

        const typeElement = valArr.filter((item) => item.match("type"));

        const type = typeElement.length
          ? typeElement[0].split(":")[1]
          : "string";

        obj.type = type;

        const valifationArray = valArr.filter((item) => !item.match("type"));
        const validations = valifationArray
          .filter((item) => item.match(":"))
          .map((item) => {
            const [method, ...args] = item.split(":");
            const newMethod = isNumericType(type) ? `${method}Number` : method;
            const arg = args.map((item) => Number(item));
            return {
              method,
              args: [
                ...arg,
                getLang(`errors.${newMethod}`, [field.label, ...arg]),
              ],
            };
          });

        obj.validations = validations;

        return obj;
      };
      schemaParams = convertToSchema();

      return generateYupSchema(schemaParams);
    }

    function generateYupSchema(schemaParams: any): any {
      // Loop through the schemaParams object
      const { type, required, validations, label, nullable } = schemaParams;

      // @ts-ignore

      let fieldSchema: Yup.Schema<any>;

      if (required && type === "string") {
        // @ts-ignore
        fieldSchema = Yup[type]().required(getLang("errors.required", [label]));
        // @ts-ignore
      } else if (nullable && type === "string") {
        // @ts-ignore
        fieldSchema = Yup[type]().nullable(true);
        // @ts-ignore
      } else if (type === "ip") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .required(getLang("errors.required", [label]))
          .matches(
            /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/,
            getLang("errors.notValid", [label])
          );
        // @ts-ignore
      } else if (required && type === "email") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .required(getLang("errors.required", [label]))
          .email(getLang("errors.notValid", [label]));
      } else if (nullable && type === "email") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .nullable()
          .notRequired()
          .email(getLang("errors.notValid", [label]));
      } else if (required && type === "url") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .required(getLang("errors.required", [label]))
          .url(getLang("errors.notValid", [label]));
      } else if (nullable && type === "url") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .nullable()
          .notRequired()
          .url(getLang("errors.notValid", [label]));
      } else if (required && type === "persianText") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .required(getLang("errors.required", [label]))
          .matches(
            /^[\u0600-\u06FF\s]+$/,
            getLang("errors.persianText", [label])
          );
      } else if (nullable && type === "persianText") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .nullable()
          .notRequired()
          .matches(
            /^[\u0600-\u06FF\s]*$/,
            getLang("errors.persianText", [label])
          );
      } else if (required && type === "englishText") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .required(getLang("errors.required", [label]))
          .matches(/^[A-Za-z\s]+$/, getLang("errors.englishText", [label]));
      } else if (nullable && type === "englishText") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .nullable()
          .notRequired()
          .matches(/^[A-Za-z\s]*$/, getLang("errors.englishText", [label]));
      } else if (required && type === "mobile") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .required(getLang("errors.required", [label]))
          .matches(/^09[0-9]{9}$/, getLang("errors.notValid", [label]));
      } else if (nullable && type === "mobile") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .nullable()
          .notRequired()
          .matches(/^09[0-9]{9}$/, getLang("errors.notValid", [label]));
      } else if (required && type === "phone") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .required(getLang("errors.required", [label]))
          .matches(/^0[0-9]{10}$/, getLang("errors.notValid", [label]));
      } else if (nullable && type === "phone") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .nullable()
          .notRequired()
          .matches(/^0[0-9]{10}$/, getLang("errors.notValid", [label]));
      } else if (required && type === "number") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .required(getLang("errors.required", [label]))
          .matches(
            /^[\u06F0-\u06F9\u0030-\u0039]+$/,
            getLang("error_numeric", [label])
          );
        // @ts-ignore
      } else if (nullable && type === "number") {
        // @ts-ignore
        fieldSchema = Yup[type]()
          .nullable(true)
          .notRequired()
          .transform((value: any, originalValue: any) =>
            originalValue === "" ? null : value
          )
          .typeError(getLang("error_numeric", [label]))
          .test(
            "is-number",
            getLang("error_numeric", [label]),
            (value: any) => {
              if (value === null || value === undefined || value === "")
                return true;
              return !isNaN(value);
            }
          );
      } else if (required && type === "array") {
        fieldSchema = Yup.array()
          .required(getLang("errors.required", [label]))
          .min(1, getLang("errors.required", [label]));
      } else if (required && type === "int") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .required(getLang("errors.required", [label]))
          .matches(
            /^[\u06F0-\u06F9\u0030-\u0039]+$/,
            getLang("error_numeric", [label])
          )
          .test((value, ctx) => {
            const p2e = (s: any) =>
              s.replace(/[۰-۹]/g, (d: any) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));

            for (const validation of validations) {
              const type = pathOr(null, ["method"], validation);
              if (type === "max") {
                const currentNumber = p2e(value);
                const [max, message] = pathOr([0, ""], ["args"], validation);
                if (currentNumber > max) {
                  return ctx.createError({ message });
                }
              } else if (type === "min") {
                const currentNumber = p2e(value);
                const [min, message] = pathOr([0, ""], ["args"], validation);
                if (currentNumber < min) {
                  return ctx.createError({ message });
                }
              }
            }
            return true;
          });
      } else if (nullable && type === "int") {
        // @ts-ignore
        fieldSchema = Yup.string()
          .nullable()
          .transform((value: any, originalValue: any) =>
            originalValue === "" ||
            originalValue === null ||
            originalValue === undefined
              ? null
              : value
          )
          .test("is-numeric", getLang("error_numeric", [label]), (value) => {
            // اگر مقدار نال باشد یعنی از ولیدیشن رد شود
            if (value === null || value === undefined || value === "")
              return true;
            // چک کردن اینکه آیا فقط شامل اعداد فارسی یا انگلیسی است
            return /^[\u06F0-\u06F9\u0030-\u0039]+$/.test(value);
          })
          .test("min-max-check", "", (value, ctx) => {
            if (value === null || value === undefined || value === "")
              return true;

            const p2e = (s: string) =>
              parseInt(
                s.replace(/[۰-۹]/g, (d: string) =>
                  "۰۱۲۳۴۵۶۷۸۹".indexOf(d).toString()
                ),
                10
              );

            const numericValue = p2e(value);

            for (const validation of validations) {
              const type = pathOr(null, ["method"], validation);
              const [limit, message] = pathOr([0, ""], ["args"], validation);

              if (type === "max" && numericValue > limit) {
                return ctx.createError({ message });
              }
              if (type === "min" && numericValue < limit) {
                return ctx.createError({ message });
              }
            }
            return true;
          });
      } else if (required && isDecimalType(type)) {
        // @ts-ignore
        fieldSchema = Yup.string()
          .required(getLang("errors.required", [label]))
          .matches(DECIMAL_PATTERN, getLang("error_numeric", [label]))
          .test("min-max-check", "", (value, ctx) => {
            const numericValue = parseDecimalValue(value);
            if (Number.isNaN(numericValue)) {
              return ctx.createError({
                message: getLang("error_numeric", [label]),
              });
            }

            for (const validation of validations) {
              const validationType = pathOr(null, ["method"], validation);
              const [limit, message] = pathOr([0, ""], ["args"], validation);

              if (validationType === "max" && numericValue > limit) {
                return ctx.createError({ message });
              }
              if (validationType === "min" && numericValue < limit) {
                return ctx.createError({ message });
              }
            }
            return true;
          });
      } else if (nullable && isDecimalType(type)) {
        // @ts-ignore
        fieldSchema = Yup.string()
          .nullable()
          .transform((value: any, originalValue: any) =>
            originalValue === "" ||
            originalValue === null ||
            originalValue === undefined
              ? null
              : value
          )
          .test("is-decimal", getLang("error_numeric", [label]), (value) => {
            if (value === null || value === undefined || value === "")
              return true;
            return DECIMAL_PATTERN.test(value);
          })
          .test("min-max-check", "", (value, ctx) => {
            if (value === null || value === undefined || value === "")
              return true;

            const numericValue = parseDecimalValue(value);
            if (Number.isNaN(numericValue)) {
              return ctx.createError({
                message: getLang("error_numeric", [label]),
              });
            }

            for (const validation of validations) {
              const validationType = pathOr(null, ["method"], validation);
              const [limit, message] = pathOr([0, ""], ["args"], validation);

              if (validationType === "max" && numericValue > limit) {
                return ctx.createError({ message });
              }
              if (validationType === "min" && numericValue < limit) {
                return ctx.createError({ message });
              }
            }
            return true;
          });
      } else if (required && type === "object") {
        // @ts-ignore
        fieldSchema = Yup[type]()
          .required(getLang("errors.required", [label]))
          .typeError(getLang("errors.required", [label]));
        // @ts-ignore
      } else if (nullable && type === "object") {
        // @ts-ignore
        fieldSchema = Yup[type]()
          .nullable(true)
          .typeError(getLang("errors.required", [label]));
        // @ts-ignore
      } else if (!nullable && !!required && type === "number") {
        // @ts-ignore
        fieldSchema = Yup[type]().typeError(getLang("error_numeric", [label]));
      } else {
        // @ts-ignore
        fieldSchema = Yup[type]();
      }

      //   Yup[type]().required(getLang("errors.required", [label]))
      //  // @ts-ignore
      //   Yup[type]();

      // Apply additional validations if provided

      if (validations && validations.length > 0 && !isNumericType(type)) {
        validations.forEach((validation: any) => {
          // @ts-ignore
          fieldSchema = fieldSchema[validation.method](...validation.args);
        });
      }

      return fieldSchema;
    }

    globalProperties.$generateYupSchema = createYupSchema;
    app.provide("createYupSchema", createYupSchema);
  },
};

export default schemaGenerator;
